import { useMemo } from "react";
import { motion } from "framer-motion";
import { Bed, Users, CalendarDays, Euro, Gift, StickyNote } from "lucide-react";
import { useTranslation } from "react-i18next";

interface OrderSummaryProps {
  variant: {
    name: string;
    variantNote?: string;
    checkIn?: string;
    checkOut?: string;
    adultsNumber?: number;
    childrenNumber?: number;
    room?: Array<{
      description?: string;
      code?: string;
      guestNumber?: number;
      rate?: Array<{
        board?: Array<{
          description?: string;
          riep?: { totale?: number; totale_ori?: number };
        }>;
      }>;
    }>;
    services?: Array<{
      description: string;
      amount: number;
      quantity?: number;
    }>;
  };
  depositAmount?: number | null;
  depositLabel?: string;
  adultsNumber?: number;
  childrenNumber?: number;
}

export default function OrderSummary({
  variant,
  depositAmount,
  depositLabel,
  adultsNumber,
  childrenNumber,
}: OrderSummaryProps) {
  const { t } = useTranslation("checkout");

  const roomsTotal =
    variant.room?.reduce(
      (sum, r) =>
        sum +
        (r.rate?.reduce(
          (s, rt) =>
            s +
            (rt.board?.reduce((b, bd) => b + (bd.riep?.totale || 0), 0) || 0),
          0,
        ) || 0),
      0,
    ) || 0;

  const servicesTotal =
    variant.services?.reduce(
      (sum, s) => sum + (s.amount || 0) * (s.quantity || 1),
      0,
    ) || 0;

  const total = roomsTotal + servicesTotal;

  const effectiveAdults = adultsNumber ?? variant.adultsNumber;
  const effectiveChildren = childrenNumber ?? variant.childrenNumber;

  const guestSummary = useMemo(() => {
    if (effectiveAdults != null && effectiveAdults > 0) {
      const adultsText = `${effectiveAdults} ${
        effectiveAdults === 1
          ? (t("confirmation.adult") || "adulto")
          : (t("confirmation.adults") || "adulti")
      }`;
      if (effectiveChildren != null && effectiveChildren > 0) {
        const childrenText = `${effectiveChildren} ${
          effectiveChildren === 1
            ? (t("confirmation.child") || "bambino")
            : (t("confirmation.children") || "bambini")
        }`;
        return `${adultsText}, ${childrenText}`;
      }
      return adultsText;
    }

    const roomGuests =
      variant.room?.reduce((s, r) => s + (r.guestNumber || 0), 0) || 0;

    if (roomGuests > 0) {
      return `${roomGuests} ${
        roomGuests === 1
          ? (t("confirmation.guest") || "ospite")
          : (t("confirmation.guestsLabel") || "ospiti")
      }`;
    }

    if (effectiveChildren != null && effectiveChildren > 0) {
      return `${effectiveChildren} ${
        effectiveChildren === 1
          ? (t("confirmation.child") || "bambino")
          : (t("confirmation.children") || "bambini")
      }`;
    }

    return `1 ${t("confirmation.adult") || "adulto"}`;
  }, [effectiveAdults, effectiveChildren, variant.room, t]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border bg-card p-6 md:p-7 space-y-6 shadow-sm"
      aria-label="Riepilogo della prenotazione"
    >
      <div className="border-b pb-4">
        <h3 className="font-semibold text-xl tracking-tight">
          Riepilogo prenotazione
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Dettagli delle camere e servizi selezionati
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-3 text-sm">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Bed className="h-4 w-4 text-primary" />
          </div>
          <span className="font-medium text-foreground">{variant.name}</span>
        </div>

        <div className="flex items-center gap-3 text-sm">
          <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <Users className="h-4 w-4 text-muted-foreground" />
          </div>
          <span className="text-muted-foreground">
            {guestSummary}
          </span>
        </div>

        {variant.checkIn && (
          <div className="flex items-center gap-3 text-sm">
            <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
            </div>
            <span className="text-muted-foreground">
              {new Date(variant.checkIn).toLocaleDateString("it-IT")}
              {variant.checkOut
                ? ` - ${new Date(variant.checkOut).toLocaleDateString("it-IT")}`
                : ""}
            </span>
          </div>
        )}

        <div className="space-y-3 pt-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
            Camere & Tariffe
          </span>

          {variant.room?.map((r, i) => (
            <div
              key={i}
              className="flex items-start justify-between gap-4 rounded-xl border bg-muted/20 hover:bg-muted/30 p-4 transition-colors text-sm"
            >
              <div className="space-y-1.5 min-w-0 flex-1">
                <p className="font-medium leading-snug text-foreground break-words">
                  {r.description || r.code || "Camera"}
                </p>
                {r.rate?.[0]?.board?.[0]?.description ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-muted text-muted-foreground font-normal">
                    {r.rate[0].board[0].description}
                  </span>
                ) : null}
              </div>
              <div className="shrink-0 text-right pt-0.5">
                <span className="font-semibold text-base tabular-nums text-foreground">
                  {(r.rate?.[0]?.board?.[0]?.riep?.totale || 0).toFixed(2)} €
                </span>
              </div>
            </div>
          ))}

          {variant.services && variant.services.length > 0 && (
            <div className="space-y-2 pt-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
                Servizi inclusi / opzionali
              </span>
              {variant.services.map((s, i) => (
                <div
                  key={`srv-${i}`}
                  className="flex items-center justify-between gap-3 rounded-xl border bg-emerald-50/40 dark:bg-emerald-950/20 p-3.5 text-sm border-emerald-200 dark:border-emerald-800/50"
                >
                  <span className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300 font-medium">
                    <Gift className="h-4 w-4 text-emerald-600 shrink-0" />
                    <span>{s.description}</span>
                    {s.quantity && s.quantity > 1 ? (
                      <span className="text-xs bg-emerald-100 dark:bg-emerald-900 px-1.5 py-0.5 rounded font-normal">
                        ×{s.quantity}
                      </span>
                    ) : null}
                  </span>
                  <span className="font-semibold text-emerald-700 dark:text-emerald-400 tabular-nums shrink-0">
                    {s.amount > 0
                      ? `${(s.amount * (s.quantity || 1)).toFixed(2)} €`
                      : "Incluso"}
                  </span>
                </div>
              ))}
            </div>
          )}

          {variant.variantNote && (
            <div className="rounded-xl border border-amber-200/80 bg-amber-50/60 dark:border-amber-900/60 dark:bg-amber-950/20 p-3.5 space-y-1.5">
              <span className="text-xs font-semibold flex items-center gap-1.5 text-amber-900 dark:text-amber-200">
                <StickyNote className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                Note variante:
              </span>
              <p className="text-xs text-amber-950/90 dark:text-amber-100/90 whitespace-pre-line leading-relaxed">
                {variant.variantNote}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="border-t pt-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-base font-semibold text-foreground">
            <Euro className="h-5 w-5 text-muted-foreground" /> Totale soggiorno
          </span>
          <span
            className={`text-2xl font-bold tabular-nums ${
              depositAmount != null &&
              depositAmount > 0 &&
              depositAmount < total
                ? "text-muted-foreground text-lg"
                : "text-emerald-600 dark:text-emerald-400"
            }`}
          >
            {total.toFixed(2)} €
          </span>
        </div>

        {depositAmount != null &&
          depositAmount > 0 &&
          depositAmount < total && (
            <div className="pt-2 border-t border-dashed space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                  Caparra richiesta {depositLabel ? `(${depositLabel})` : ""}
                </span>
                <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                  {depositAmount.toFixed(2)} €
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Saldo rimanente in hotel</span>
                <span className="tabular-nums font-medium">
                  {(total - depositAmount).toFixed(2)} €
                </span>
              </div>
            </div>
          )}
      </div>
    </motion.div>
  );
}
